import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom-v5-compat";

import {
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
  JOB_OPPORTUNITY_STATUSES,
} from "lookup";

import { JobsContext, MatchContext, ModalContext } from "context/providers";

import {
  HitActionButtons,
  CollapsibleSection,
  MatchActionButtons,
  UserContacts,
  UserInfo,
} from "./molecules";
import ListViewRowActions from "../UserTableListView/ListViewRowActions";
import { TABS } from "context/constants/common";
import HitBadDataError from "./molecules/BadDataError";
import { getUserName } from "utils/helpers/users";
import { cn } from "utils";
import RankDetails from "../MatchAgentListView/HitCard/components/RankDetails";
import { customComparator } from "utils/common";
import SvgIcon from "components/base/SvgIcon";
import SkillBadge from "components/SkillBadge";
import LogoAvatar from "components/LogoAvatar";
import CareerPlaceholder from "components/CareerPlaceholder";
import { getCompanyDomain } from "utils/getCompanyDomain";
import {
  PROFILE_SECTION_TITLE_CLASSNAME,
  PROFILE_META_LINE_CLASSNAME,
  PROFILE_MORE_LINK_CLASSNAME,
  getExperienceSortRank,
} from "./constants";

const CareerTrackInsights = ({ rankDetails }) => {
  if (rankDetails?.score === undefined) {
    return null;
  }

  return (
    <section className="mt-8 w-full">
      <h3
        className={cn(
          "mb-4 flex items-center gap-2",
          PROFILE_SECTION_TITLE_CLASSNAME
        )}
      >
        <SvgIcon type="aiSparkles" className="h-4 w-4 fill-[#5A5E6E]" />
        Career track analysis
      </h3>
      <RankDetails {...rankDetails} defaultOpen variant="profile" />
    </section>
  );
};

const MAX_SUMMARY_CHAPTERS = 3;

const getSkillKey = (skill) => `${skill.name}_${skill.category}`;

const getSummarySkills = (hit, jobOpp) => {
  const talentSkillsMap = {};

  for (const entry of [
    ...(hit.careers || []),
    ...(hit.resumeWorkHistory || []),
  ]) {
    for (const skill of entry?.TKSkills || []) {
      if (skill.category === "CERTIFICATION") {
        continue;
      }

      const skillKey = getSkillKey(skill);
      const existingSkill = talentSkillsMap[skillKey];

      if (
        !existingSkill ||
        getExperienceSortRank(skill.experience) <
          getExperienceSortRank(existingSkill.experience)
      ) {
        talentSkillsMap[skillKey] = skill;
      }
    }
  }

  const talentSkills = Object.values(talentSkillsMap);
  const jobSkillKeys = new Set(
    [
      ...(jobOpp?.TKSkills || []),
      ...(jobOpp?.intelSkills || []),
      ...(jobOpp?.optionalTKSkills || []),
    ]
      .filter((skill) => skill.category !== "CERTIFICATION")
      .map(getSkillKey)
  );

  return talentSkills
    .map((skill) => ({
      ...skill,
      isJobMatch: jobSkillKeys.has(getSkillKey(skill)),
    }))
    .sort((a, b) => {
      const matchDiff = Number(b.isJobMatch) - Number(a.isJobMatch);

      if (matchDiff !== 0) {
        return matchDiff;
      }

      const experienceDiff =
        getExperienceSortRank(a.experience) -
        getExperienceSortRank(b.experience);

      if (experienceDiff !== 0) {
        return experienceDiff;
      }

      return (a.name || "").localeCompare(b.name || "");
    });
};

const formatSummaryYearRange = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const startText =
    start && !Number.isNaN(start.valueOf()) ? start.getFullYear() : "-";
  const endText =
    end && !Number.isNaN(end.valueOf()) ? end.getFullYear() : "Present";

  return `${startText} - ${endText}`;
};

const getCareerChapters = (hit) =>
  [...(hit.careers || []), ...(hit.resumeWorkHistory || [])].sort(
    customComparator
  );

const getCompanyName = (chapter) => chapter.companyName || chapter.client || "";

const PROFILE_BODY_OFFSET_CLASSNAME = "pl-2";

const SKILL_ROW_GAP = 8; // matches gap-2 (0.5rem)

// Renders the summary skills as a single responsive line: the number of visible
// badges is measured from the container width (not a hardcoded cap), with a "+N"
// chip and a toggle to expand into the full wrapped list.
const SummarySkills = ({ skills, showAllSkills, setShowAllSkills }) => {
  const containerRef = useRef(null);
  const probeRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(skills.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) {
      return undefined;
    }

    const measure = () => {
      const available = container.clientWidth;
      const badgeEls = Array.from(probe.querySelectorAll("[data-skill-badge]"));
      const chipEl = probe.querySelector("[data-skill-chip]");
      if (!badgeEls.length || !available) {
        setVisibleCount(skills.length);
        return;
      }

      const widths = badgeEls.map((el) => el.offsetWidth);
      const chipWidth = chipEl ? chipEl.offsetWidth : 0;

      // Greedily fit badges on a single line.
      let used = 0;
      let fit = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const next = used + (fit > 0 ? SKILL_ROW_GAP : 0) + widths[i];
        if (next > available) {
          break;
        }
        used = next;
        fit += 1;
      }

      // If some are hidden, make room for the "+N" chip by dropping badges
      // until the chip fits alongside on the same line.
      if (fit < skills.length) {
        while (fit > 0) {
          const badgesWidth =
            widths.slice(0, fit).reduce((sum, w) => sum + w, 0) +
            SKILL_ROW_GAP * (fit - 1);
          if (badgesWidth + SKILL_ROW_GAP + chipWidth <= available) {
            break;
          }
          fit -= 1;
        }
      }

      setVisibleCount(fit);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [skills]);

  const hiddenSkillCount = Math.max(skills.length - visibleCount, 0);
  const visibleSkills = showAllSkills ? skills : skills.slice(0, visibleCount);

  return (
    <div ref={containerRef} className="relative min-w-0">
      {/* Hidden probe: renders every badge (+ a chip) off-flow so their natural
          widths can be measured against the real container width. */}
      <div
        ref={probeRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex h-0 gap-2 overflow-hidden"
      >
        {skills.map((skill) => (
          <div
            data-skill-badge
            key={`measure-skill-${skill.name}-${skill.category}`}
          >
            <SkillBadge
              skillName={skill.name}
              category={skill.category}
              experience={skill.experience}
              noUppercase
              size="sm"
            />
          </div>
        ))}
        <span
          data-skill-chip
          className="inline-flex h-6 items-center rounded-md px-2 text-xs font-semibold"
        >
          +{skills.length}
        </span>
      </div>

      <div className="flex min-w-0 flex-wrap gap-2">
        {visibleSkills.map((skill) => (
          <SkillBadge
            key={`summary-skill-${skill.name}-${skill.category}`}
            skillName={skill.name}
            category={skill.category}
            experience={skill.experience}
            noUppercase
            size="sm"
          />
        ))}
        {!showAllSkills && hiddenSkillCount > 0 && (
          <button
            type="button"
            className="inline-flex h-6 items-center rounded-md bg-slate-100 px-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
            onClick={() => setShowAllSkills(true)}
          >
            +{hiddenSkillCount}
          </button>
        )}
        {showAllSkills && hiddenSkillCount > 0 && (
          <button
            type="button"
            className="inline-flex h-6 items-center gap-1 rounded-md bg-slate-100 px-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
            onClick={() => setShowAllSkills(false)}
          >
            Less
            <SvgIcon
              type="chevronDown"
              className="h-4 w-4 rotate-180 text-slate-600 transition-transform"
            />
          </button>
        )}
      </div>
    </div>
  );
};

SummarySkills.propTypes = {
  skills: PropTypes.arrayOf(PropTypes.object).isRequired,
  showAllSkills: PropTypes.bool.isRequired,
  setShowAllSkills: PropTypes.func.isRequired,
};

const ProfileHighlights = ({ hit, jobOpp, showChapters }) => {
  const [showAllSkills, setShowAllSkills] = useState(false);
  const skills = getSummarySkills(hit, jobOpp);
  const chapters = getCareerChapters(hit).slice(0, MAX_SUMMARY_CHAPTERS);

  const renderChapters = () => {
    return chapters.map((chapter) => {
      const companyName = getCompanyName(chapter);
      const title = chapter.title || chapter.name || "Untitled";
      const yearRange = formatSummaryYearRange(
        chapter.startDate,
        chapter.endDate
      );

      return (
        <div
          key={`summary-chapter-${chapter.id || title}`}
          className={cn(
            "grid w-full min-w-0 items-center gap-3 sm:grid-cols-[minmax(0,1fr)_max-content]",
            PROFILE_META_LINE_CLASSNAME
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            <LogoAvatar
              name={companyName || title}
              domain={getCompanyDomain(chapter)}
              size="xs"
              placeHolder={<CareerPlaceholder size="xs" />}
            />
            <div className="min-w-0">
              <span className="break-words">{title}</span>
              {companyName && (
                <span className="break-words text-slate-700">
                  {" "}
                  at {companyName}
                </span>
              )}
            </div>
          </div>
          <span className="sm:text-right">{yearRange}</span>
        </div>
      );
    });
  };

  return (
    <div
      className={cn(
        "mt-4 min-w-0 max-w-full space-y-4",
        PROFILE_BODY_OFFSET_CLASSNAME
      )}
    >
      <SummarySkills
        skills={skills}
        showAllSkills={showAllSkills}
        setShowAllSkills={setShowAllSkills}
      />

      {showChapters && chapters.length > 0 && (
        <div className="max-w-4xl space-y-2">{renderChapters()}</div>
      )}
    </div>
  );
};

const UserCardResult = ({
  activeTabName,
  hit,
  showActions,
  collectionKey,
  showCareerTrackAnalysis,
  showProfileActions,
  showFooterActions,
}) => {
  const { showModal } = useContext(ModalContext);
  const { createMatch, updateMatch, removeMatch } = useContext(MatchContext);
  const { jobOpp, convertJobLongListToMatch } = useContext(JobsContext);

  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const currentTab = params.get("tab");

  const [isLoading, setIsLoading] = useState({
    [JOB_APPLICATION_MATCH_STATUS.MATCHED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: false,
    [JOB_APPLICATION_MATCH_STATUS.DELETED]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.NULL]: false,
    [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]: false,
    [JOB_APPLICATION_MATCH_STATUS.APPLIED]: false,
    [JOB_APPLICATION_MATCH_STATUS.ACCEPTED]: false,
  });
  const [isProfileExpanded, setIsProfileExpanded] = useState(false);

  const { applicationId, jobOpportunityId } = useMemo(() => {
    const app = Array.isArray(hit.applications)
      ? hit.applications.find((a) => a.jobTypeId === jobOpp.jobType?.id)
      : undefined;

    return {
      applicationId:
        hit?.match?.applicationId ||
        hit?.associatedMatch?.applicationId ||
        app?.id ||
        `User#${hit.id}`,
      jobOpportunityId: jobOpp?.id,
    };
  }, [hit, jobOpp]);

  const handleMatchAction = async (args, isUpdate) => {
    if (args.status === JOB_APPLICATION_MATCH_STATUS.MATCHED) {
      // passing the value to the modal so it can be viewed in the modal
      showModal({
        type: "match",
        applicationId,
        jobOpportunityId,
        userId: hit.id,
        userIsUnclaimed: hit.isUnclaimed,
        collectionKey,
        status: args.status,
        isUpdate,
        isJobLongList: hit.match?.canConvertToMatch,
        resumeLocation: hit.match?.resumeLocation,
        hitStatus: hit.match?.status || hit.associatedMatch?.status,
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, [args.status]: true }));

    if (hit.isUnclaimed) {
      args.unclaimedWhenMatched = true;
    }

    const matchAttrs = {
      ...args,
      ...(hit.match?.canConvertToMatch && hit.match?.resumeLocation
        ? { resumeLocation: hit.match.resumeLocation }
        : {}),
    };

    try {
      if (isUpdate) {
        await updateMatch(applicationId, jobOpportunityId, matchAttrs);
      } else {
        await createMatch(jobOpportunityId, hit.id, matchAttrs);
        if (hit.match.canConvertToMatch) {
          await convertJobLongListToMatch(jobOpportunityId, hit.id);
        }
      }
    } catch (error) {
      console.log("matchAction error: ", error);
    } finally {
      setIsLoading((prev) => ({ ...prev, [args.status]: false }));
    }
  };

  const updateExistingMatch = async (args) => {
    if (
      args?.subStatus === JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST &&
      jobOpp.isDMP
    ) {
      showModal({
        type: "finalist",
        matchParams: args,
        match: hit.match,
        applicationId,
        jobOpportunityId,
        collectionKey,
        userId: hit.match?.userId || hit.id,
        isUpdate: true,
      });
      return;
    }

    if (args?.status === JOB_APPLICATION_MATCH_STATUS.MATCHED) {
      // passing the value to the modal so it can be viewed in the modal
      showModal({
        type: "match",
        applicationId,
        jobOpportunityId,
        collectionKey,
        status: args?.status,
        hitStatus: hit.match?.status,
        userId: hit.match?.userId || hit.id,
        isUpdate: true,
      });
      return;
    }

    setIsLoading((prev) => ({
      ...prev,
      [args.status || args.subStatus]: true,
    }));

    try {
      await updateMatch(applicationId, jobOpportunityId, args);
    } catch (error) {
      console.log("matchAction error: ", error);
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        [args.status || args.subStatus]: false,
      }));
    }
  };

  const removeExistingMatch = async () => {
    const confirmRemove = window.confirm(
      `Are you sure you want to delete ${getUserName(hit)} match?`
    );

    if (confirmRemove) {
      setIsLoading((prev) => ({
        ...prev,
        [JOB_APPLICATION_MATCH_STATUS.DELETED]: true,
      }));

      try {
        await removeMatch(applicationId, jobOpportunityId);
      } catch (error) {
        console.log("removeExistingMatch error: ", error);
      } finally {
        setIsLoading((prev) => ({
          ...prev,
          [JOB_APPLICATION_MATCH_STATUS.DELETED]: false,
        }));
      }
    }
  };

  const hitHasApiErrors =
    hit?.apiErrors ||
    hit?.associatedMatch?.apiErrors ||
    hit?.associatedMatch?.user?.apiErrors;

  const getFormatUniqueApiErrors = (hit) => {
    return [
      ...(hit?.associatedMatch?.apiErrors || []),
      ...(hit?.apiErrors || []),
      ...(hit?.associatedMatch?.user?.apiErrors || []),
    ].filter(
      (error, index, self) =>
        index === self.findIndex((e) => e.message === error.message)
    );
  };

  const actionBar = (
    <ListViewRowActions
      hit={hit}
      row={{ original: hit }}
      activeTabName={activeTabName}
      collectionKey={collectionKey}
      tabUsesMatches={collectionKey === "match"}
      hideMatchActions={collectionKey === "hits" && !showActions}
    />
  );

  // data-candidate-id is a stable identity hook for E2E: it lets a test follow one
  // specific candidate across tabs instead of asserting that "some card" exists.
  return (
    <div
      data-testid="candidate-card"
      data-candidate-id={hit?.id}
      className={cn(
        "talent-card",
        "relative mb-4 flex min-w-0 max-w-full flex-col overflow-x-hidden rounded-lg bg-white font-inter"
      )}
    >
      <div className="box-border flex w-full min-w-0 flex-col gap-4 rounded-t-lg border border-[#E5E7EB] p-5">
        <div className="flex w-full min-w-0 flex-wrap gap-x-4 gap-y-4 md:flex-nowrap md:items-start">
          <div className="w-[125px] shrink-0">
            <UserContacts
              hit={hit}
              rankDetails={showCareerTrackAnalysis ? hit.rankDetails : null}
            />
          </div>

          <div className="min-w-0 w-full flex-1">
            <UserInfo
              hit={hit}
              collectionKey={collectionKey}
              activeTabName={activeTabName}
              showProfileActions={showProfileActions}
              actions={showProfileActions ? actionBar : null}
            />
          </div>
        </div>

        <ProfileHighlights
          hit={hit}
          jobOpp={jobOpp}
          showChapters={!isProfileExpanded}
        />
        {isProfileExpanded && (
          <div className={cn("min-w-0 w-full", PROFILE_BODY_OFFSET_CLASSNAME)}>
            {jobOpp && (
              <CollapsibleSection
                activeTabName={activeTabName}
                hit={hit}
                allowLocalHightLight={
                  !currentTab || currentTab === TABS.SEARCHRESULTS
                }
                careerTrackAnalysis={
                  showCareerTrackAnalysis ? (
                    <CareerTrackInsights rankDetails={hit.rankDetails} />
                  ) : null
                }
              />
            )}
          </div>
        )}
        <button
          type="button"
          className={PROFILE_MORE_LINK_CLASSNAME}
          onClick={() => setIsProfileExpanded((prev) => !prev)}
        >
          {isProfileExpanded ? "Less" : "View more"}
          <SvgIcon
            type="chevronDown"
            className={cn("h-4 w-4 text-slate-600 transition-transform", {
              "rotate-180": isProfileExpanded,
            })}
          />
        </button>
      </div>

      {showFooterActions || hitHasApiErrors ? (
        <div className="rounded-b-lg border-t border-grey-900 py-3 bg-gray-50">
          {hitHasApiErrors ? (
            <HitBadDataError apiErrors={getFormatUniqueApiErrors(hit)} />
          ) : showFooterActions ? (
            <div className="flex justify-center min-h-[45px]">
              {hit?.match?.applicationId ? (
                <MatchActionButtons
                  match={hit.match}
                  isLoading={isLoading}
                  isJobStatusActive={
                    jobOpp.status === JOB_OPPORTUNITY_STATUSES.ACTIVE
                  }
                  activeTabName={activeTabName}
                  updateMatch={updateExistingMatch}
                  removeMatch={removeExistingMatch}
                  hasPrimaryMatcher={jobOpp.torcOwner?.username}
                />
              ) : (
                <HitActionButtons
                  hit={hit}
                  isLoading={isLoading}
                  showActions={showActions}
                  matchAction={handleMatchAction}
                  jobType={jobOpp.jobType}
                  isJobStatusActive={
                    jobOpp.status === JOB_OPPORTUNITY_STATUSES.ACTIVE
                  }
                  hasPrimaryMatcher={jobOpp.torcOwner?.username}
                />
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

UserCardResult.propTypes = {
  hit: PropTypes.object.isRequired,
  showActions: PropTypes.bool,
  collectionKey: PropTypes.string,
  activeTabName: PropTypes.string,
  showCareerTrackAnalysis: PropTypes.bool,
  showProfileActions: PropTypes.bool,
  showFooterActions: PropTypes.bool,
};

UserCardResult.defaultProps = {
  showActions: false,
  collectionKey: "hits",
  activeTabName: "SEARCHRESULTS",
  showCareerTrackAnalysis: false,
  showProfileActions: true,
  showFooterActions: false,
};

export default UserCardResult;
